import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Custom event for language updates
const LANGUAGE_UPDATED_EVENT = 'languageUpdated';

export const LanguageProvider = ({ children }) => {
  // Initialize as undefined to distinguish between "not loaded yet" and "loaded but no preference set"
  const [language, setLanguage] = useState(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLanguagePreference = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axiosInstance.get('/language/preference');
          if (response.data.success) {
            // Explicitly set to null if no preference is set
            setLanguage(response.data.data.preferredLanguage || null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch language preference:', error);
        // Set to null on error to indicate no preference
        setLanguage(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLanguagePreference();
  }, []);

  const updateLanguage = async (newLanguage) => {
    try {
      const response = await axiosInstance.post('/language/preference', {
        language: newLanguage
      });

      if (response.data.success) {
        setLanguage(newLanguage);
        // Dispatch custom event when language is successfully updated
        window.dispatchEvent(new CustomEvent(LANGUAGE_UPDATED_EVENT));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update language:', error);
      throw error;
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      updateLanguage, 
      loading,
      // Add a helper value to check if language needs to be set
      needsLanguageSelection: !loading && (!language || language === '')
    }}>
      {children}
    </LanguageContext.Provider>
  );
}; 